/**
 * The small "Add a detour to here?" question shown where a day's flag was
 * dropped off the line. Resolves true for the detour, false for anything else
 * (Cancel, Escape, or carrying on with the map).
 */

interface PromptMap {
  project(lngLat: [number, number]): { x: number; y: number };
  getContainer(): HTMLElement;
  on(type: string, listener: (...args: any[]) => void): unknown;
  off(type: string, listener: (...args: any[]) => void): unknown;
}

export interface DetourPromptText {
  question: string;
  add: string;
  cancel: string;
}

export const DETOUR_PROMPT_CLASS = 'detour-prompt';

export function askToAddDetour(map: PromptMap, coord: [number, number], text: DetourPromptText): Promise<boolean> {
  return new Promise(resolve => {
    const prompt = document.createElement('div');
    prompt.className = `${DETOUR_PROMPT_CLASS} shadow`;
    prompt.setAttribute('role', 'dialog');
    prompt.setAttribute('aria-label', text.question);
    Object.assign(prompt.style, {
      position: 'absolute',
      zIndex: '5',
      transform: 'translate(-50%, calc(-100% - 22px))',
      background: 'var(--bs-body-bg, #fff)',
      color: 'var(--bs-body-color, #212529)',
      borderRadius: '10px',
      padding: '10px 12px',
      fontSize: '14px',
      whiteSpace: 'nowrap',
    });

    const question = document.createElement('div');
    question.textContent = text.question;
    question.style.marginBottom = '8px';
    question.style.fontWeight = '600';

    const buttons = document.createElement('div');
    buttons.style.display = 'flex';
    buttons.style.gap = '8px';
    const add = document.createElement('button');
    add.type = 'button';
    add.className = 'btn btn-sm btn-primary';
    add.textContent = text.add;
    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'btn btn-sm btn-outline-secondary';
    cancel.textContent = text.cancel;
    buttons.append(add, cancel);
    prompt.append(question, buttons);

    const place = () => {
      const point = map.project(coord);
      prompt.style.left = `${point.x}px`;
      prompt.style.top = `${point.y}px`;
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish(false);
    };
    // Starting anything else on the map is a "no".
    const onMapPress = () => finish(false);

    let finished = false;
    function finish(answer: boolean) {
      if (finished) return;
      finished = true;
      prompt.remove();
      document.removeEventListener('keydown', onKey);
      map.off('move', place);
      map.off('mousedown', onMapPress);
      map.off('touchstart', onMapPress);
      resolve(answer);
    }

    add.addEventListener('click', () => finish(true));
    cancel.addEventListener('click', () => finish(false));
    // Pressing the prompt itself mustn't count as pressing the map behind it.
    prompt.addEventListener('mousedown', e => e.stopPropagation());
    prompt.addEventListener('touchstart', e => e.stopPropagation());
    document.addEventListener('keydown', onKey);
    map.on('move', place);
    map.on('mousedown', onMapPress);
    map.on('touchstart', onMapPress);

    place();
    map.getContainer().appendChild(prompt);
    add.focus();
  });
}
